import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, Loader2 } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  date: "",
  time: "",
  origin: "",
  destination: "",
  passengers: 1,
  notes: "",
};

const inputCls =
  "w-full rounded-xl border border-white/15 bg-[#070709] px-4 py-3.5 text-sm text-white outline-none transition-colors placeholder:text-slate-600 focus:border-amber-400 [color-scheme:dark]";
const labelCls =
  "mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.25em] text-slate-500";

export const BookingForm = () => {
  const { t } = useLang();
  const b = t.booking;
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const origin = form.origin || b.origins[0];

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/booking`, {
        ...form,
        origin,
        passengers: Number(form.passengers),
      });
      toast.success(b.success);
      setForm(EMPTY);
    } catch {
      toast.error(b.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="prenota" className="relative overflow-hidden py-28">
      <span
        aria-hidden="true"
        className="text-outline-faint pointer-events-none absolute right-0 top-4 select-none whitespace-nowrap font-display text-[16vw] font-black uppercase leading-none opacity-50"
      >
        {b.watermark}
      </span>
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading eyebrow={b.eyebrow} title={b.title} accent={b.accent} />
        <Reveal>
          <form
            onSubmit={submit}
            data-testid="booking-form"
            className="rounded-3xl border border-amber-400/20 bg-[#101017] p-8 sm:p-12"
          >
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              <label className="block">
                <span className={labelCls}>{b.date}</span>
                <input data-testid="booking-form-date" type="date" required value={form.date} onChange={set("date")} className={inputCls} />
              </label>
              <label className="block">
                <span className={labelCls}>{b.time}</span>
                <input data-testid="booking-form-time" type="time" required value={form.time} onChange={set("time")} className={inputCls} />
              </label>
              <label className="block">
                <span className={labelCls}>{b.origin}</span>
                <select data-testid="booking-form-origin" value={origin} onChange={set("origin")} className={inputCls}>
                  {b.origins.map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className={labelCls}>{b.passengers}</span>
                <select data-testid="booking-form-passengers" value={form.passengers} onChange={set("passengers")} className={inputCls}>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
            </div>
            <label className="mt-5 block">
              <span className={labelCls}>{b.destination}</span>
              <input data-testid="booking-form-destination" required value={form.destination} onChange={set("destination")} placeholder={b.destPlaceholder} className={inputCls} />
            </label>
            <div className="mt-5 grid gap-5 sm:grid-cols-3">
              <label className="block">
                <span className={labelCls}>{b.name}</span>
                <input data-testid="booking-form-name" required value={form.name} onChange={set("name")} placeholder="Mario Rossi" className={inputCls} />
              </label>
              <label className="block">
                <span className={labelCls}>{b.phone}</span>
                <input data-testid="booking-form-phone" type="tel" required value={form.phone} onChange={set("phone")} placeholder="+41 79 ..." className={inputCls} />
              </label>
              <label className="block">
                <span className={labelCls}>{b.email}</span>
                <input data-testid="booking-form-email" type="email" required value={form.email} onChange={set("email")} placeholder="mario@esempio.ch" className={inputCls} />
              </label>
            </div>
            <label className="mt-5 block">
              <span className={labelCls}>{b.notes}</span>
              <textarea data-testid="booking-form-notes" rows={3} value={form.notes} onChange={set("notes")} placeholder={b.notesPlaceholder} className={`${inputCls} resize-none`} />
            </label>
            <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <button
                type="submit"
                disabled={sending}
                data-testid="booking-form-submit"
                className="gold-glow inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 px-8 py-4 font-display text-sm font-extrabold uppercase tracking-wide text-black transition-all duration-300 hover:bg-amber-300 disabled:opacity-60"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarCheck className="h-4 w-4" />}
                {sending ? b.sending : b.submit}
              </button>
              <span className="text-xs text-slate-500">{b.immediate}</span>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
